/**
 * React hook for voice message playback.
 *
 * Wraps the Layer 1 `AudioPlayer` class and integrates with the Zustand
 * `voiceStore` to coordinate global playback ownership. Only one voice
 * message can play at a time — starting playback here will stop any
 * other active playback or recording.
 *
 * **Data flow**:
 * 1. `play()` → fetches the audio blob via `getBlob()` (from useVoiceAttachment)
 * 2. Passes the blob to `AudioPlayer.play()` which creates an HTMLAudioElement
 * 3. Registers this message as the active playback in the Zustand store
 * 4. `pause()` / `resume()` / `onSeekEnd()` control the underlying element
 * 5. On ended or ownership loss, cleans up the player and resets state
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { AudioPlayer } from "./AudioPlayer";
import { useVoiceStore } from "./voiceStore";

// ── Types ──

/**
 * Internal load state for audio playback.
 *
 * - `idle`        – No playback attempted yet.
 * - `fetching`    – Audio blob is being downloaded from the server.
 * - `buffering`   – Blob is loaded; HTMLAudioElement is buffering before play.
 * - `ready`       – Audio is playing or has been played successfully.
 * - `error`       – Blob fetch or audio decode failed.
 * - `unsupported` – Browser cannot play this audio format (e.g. WebM on old Safari).
 */
type PlaybackLoadState =
  | "idle"
  | "fetching"
  | "buffering"
  | "ready"
  | "error"
  | "unsupported";

/** Options passed to the useVoicePlayer hook */
interface UseVoicePlayerOptions {
  /** The chat this voice message belongs to */
  chatId: string;
  /** The unique message ID of the voice attachment */
  messageId: string;
  /** Async function to fetch the audio blob (supports AbortSignal for cancellation) */
  getBlob: (signal?: AbortSignal) => Promise<Blob | null>;
}

/** Return type of the useVoicePlayer hook */
export interface UseVoicePlayerReturn {
  /** True when this specific voice message is actively playing */
  isPlaying: boolean;
  /** True when this voice message is paused (can be resumed) */
  isPaused: boolean;
  /** Current load/playback state for driving UI indicators */
  loadState: PlaybackLoadState;
  /** Current playback position in seconds */
  currentTime: number;
  /** Total audio duration in seconds */
  duration: number;
  /** Start playback (fetches blob if needed, then plays) */
  play: () => Promise<void>;
  /** Pause the currently playing audio */
  pause: () => void;
  /** Resume paused audio (or restart if player was destroyed) */
  resume: () => Promise<void>;
  /** Seek to a specific time after scrubber interaction ends */
  onSeekEnd: (time: number) => void;
}

// ── Hook ──

/**
 * Computes the externally-visible load state based on ownership.
 *
 * Owners see the full internal `loadState`. Non-owners only see stable or
 * user-relevant states: `"fetching"`, `"buffering"`, `"ready"`, and
 * `"unsupported"` are surfaced as-is; all other internal states are
 * collapsed to `"idle"`.
 *
 * @param isOwner - Whether this hook instance owns the current playback.
 * @param loadState - The internal load state of this hook.
 * @returns The load state to expose to the UI component.
 */
function computeLoadState(
  isOwner: boolean,
  loadState: PlaybackLoadState,
): PlaybackLoadState {
  if (isOwner) {
    return loadState;
  }
  // "fetching" and "buffering" are local states set before ownership is
  // registered in the store — surface them so the UI can show a spinner.
  if (
    loadState === "ready" ||
    loadState === "unsupported" ||
    loadState === "fetching" ||
    loadState === "buffering"
  ) {
    return loadState;
  }
  return "idle";
}

export function useVoicePlayer({
  chatId,
  messageId,
  getBlob,
}: UseVoicePlayerOptions): UseVoicePlayerReturn {
  const {
    playingVoice,
    startPlayback,
    pausePlayback,
    resumePlayback,
    stopPlayback,
  } = useVoiceStore();

  const isOwner =
    playingVoice?.messageId === messageId && playingVoice?.chatId === chatId;

  // Refs
  /** The AudioPlayer instance (created lazily on first play) */
  const playerRef = useRef<AudioPlayer | null>(null);
  /** AbortController for cancelling in-flight blob fetches */
  const abortRef = useRef<AbortController | null>(null);
  /** Cached blob to avoid re-fetching on replay */
  const blobCacheRef = useRef<Blob | null>(null);
  /** Ref to always read the latest getBlob from inside memoized callbacks */
  const getBlobRef = useRef(getBlob);
  getBlobRef.current = getBlob;

  // State
  const [loadState, setLoadState] = useState<PlaybackLoadState>("idle");
  const [currentTime, setCurrentTime] = useState(0);
  const [audioDuration, setAudioDuration] = useState(0);

  // ── Cleanup on unmount ──

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      playerRef.current?.destroy();
      playerRef.current = null;
    };
  }, []);

  // ── Stop if another message takes ownership ──

  useEffect(() => {
    if (!isOwner) {
      abortRef.current?.abort();
      playerRef.current?.destroy();
      playerRef.current = null;
      setCurrentTime(0);
      setLoadState("idle");
      setAudioDuration(0);
    }
  }, [isOwner]);

  // ── Internal: get or create player with event wiring ──

  /**
   * Returns the existing AudioPlayer or creates a new one with event handlers.
   * The player is stored in a ref so it persists across renders.
   */
  const getPlayer = (): AudioPlayer => {
    if (playerRef.current) {
      return playerRef.current;
    }

    const player = new AudioPlayer({
      onTimeUpdate: (t) => setCurrentTime(t),
      onDurationChange: (d) => setAudioDuration(d),
      onEnded: () => {
        stopPlayback();
        setCurrentTime(0);
        setLoadState("idle");
        // Don't destroy here — cleanup already happens inside AudioPlayer.onended
        playerRef.current = null;
      },
      onError: (err) => {
        // eslint-disable-next-line no-console
        console.warn("Voice playback error:", err.message);
        setLoadState("error");
        stopPlayback();
        playerRef.current = null;
      },
    });

    playerRef.current = player;
    return player;
  };

  // ── Internal: fetch blob ──

  /**
   * Fetches the audio blob via the provided `getBlob` function.
   * Returns the cached blob if already fetched. Respects AbortSignal.
   *
   * @param signal - AbortSignal to cancel the fetch if playback is interrupted.
   * @returns The audio Blob, or null if fetch failed or was aborted.
   */
  const fetchBlob = async (signal: AbortSignal): Promise<Blob | null> => {
    if (blobCacheRef.current) {
      return blobCacheRef.current;
    }

    setLoadState("fetching");
    const blob = await getBlobRef.current(signal);

    if (signal.aborted) {
      return null;
    }

    if (!blob) {
      setLoadState("error");
      return null;
    }

    blobCacheRef.current = blob;
    return blob;
  };

  // ── Public API ──

  const play = useCallback(async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const blob = await fetchBlob(controller.signal);
      if (!blob || controller.signal.aborted) {
        return;
      }

      // Check if the browser can play this audio format before attempting.
      // On older browsers (e.g. Safari < 15) WebM/Opus is not supported.
      if (!AudioPlayer.canPlay(blob.type)) {
        setLoadState("unsupported");
        return;
      }

      setLoadState("buffering");

      const player = getPlayer();

      // Register ownership BEFORE starting playback so the store
      // reflects the correct owner while audio is loading/playing.
      startPlayback(chatId, messageId);
      await player.play(blob);

      setLoadState("ready");
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        return;
      }
      setLoadState("error");
      stopPlayback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId, messageId]);

  const pause = useCallback(() => {
    playerRef.current?.pause();
    pausePlayback();
  }, [pausePlayback]);

  const resume = useCallback(async () => {
    if (!playerRef.current) {
      await play();
      return;
    }
    try {
      await playerRef.current.resume();
      resumePlayback();
    } catch {
      stopPlayback();
    }
  }, [play, resumePlayback, stopPlayback]);

  const onSeekEnd = useCallback((time: number) => {
    setCurrentTime(time);
    playerRef.current?.seek(time);
  }, []);

  return {
    isPlaying: isOwner && playingVoice?.status === "playing",
    isPaused: isOwner && playingVoice?.status === "paused",
    loadState: computeLoadState(isOwner, loadState),
    currentTime,
    duration: audioDuration,
    play,
    pause,
    resume,
    onSeekEnd,
  };
}
